import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Calendar,
  User,
  Clock,
  Edit,
  Trash2,
  CheckCircle,
  Circle,
  PlayCircle,
  Tag,
  FolderOpen
} from 'lucide-react';
import { Task, Project } from '@/types/product-manager';
import { getTask, getProject, getProjects, updateTask, deleteTask } from '@/services/productManagerService';
import { employeeNamesService } from '@/services/employeeNames';
import CommentThread from '@/components/product-manager/CommentThread';
import ConfirmDialog from '@/components/product-manager/ConfirmDialog';
import EditTaskDialog from '@/components/product-manager/EditTaskDialog';
import ImageGallery from '@/components/product-manager/ImageGallery';
import { LinkifiedText } from '@/utils/linkRenderer';
import { Timestamp } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';

const TaskDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [assigneeDisplayName, setAssigneeDisplayName] = useState<string>('');
  const [creatorDisplayName, setCreatorDisplayName] = useState<string>('');

  useEffect(() => {
    if (taskId) {
      loadTaskData();
    }
  }, [taskId]);

  const loadTaskData = async () => {
    if (!taskId) return;

    try {
      setLoading(true);
      const taskData = await getTask(taskId);
      
      if (!taskData) {
        navigate('/product-manager/tasks');
        return;
      }

      setTask(taskData);

      // Load project data
      if (taskData.projectId) {
        const projectData = await getProject(taskData.projectId);
        setProject(projectData);
      }

      // Load all projects for edit dialog
      const projectsData = await getProjects();
      setProjects(projectsData);

      // Load display names
      if (taskData.assignedTo) {
        const assigneeName = await employeeNamesService.getNameFromEmail(taskData.assignedTo);
        setAssigneeDisplayName(assigneeName || taskData.assignedTo);
      }

      if (taskData.createdBy) {
        const creatorName = await employeeNamesService.getNameFromEmail(taskData.createdBy);
        setCreatorDisplayName(creatorName || taskData.createdBy);
      }
    } catch (error) {
      console.error('Error loading task data:', error);
      navigate('/product-manager/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: 'todo' | 'in-progress' | 'completed') => {
    if (!task) return;

    try {
      setUpdating(true);
      const updates: Partial<Task> = {
        status: newStatus,
        updatedAt: Timestamp.now() as any
      };

      if (newStatus === 'completed' && task.status !== 'completed') {
        updates.completedAt = Timestamp.now() as any;
      }

      await updateTask(task.id, updates);
      setTask({ ...task, ...updates });
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!task) return;

    try {
      setUpdating(true);
      await deleteTask(task.id);
      navigate('/product-manager/tasks');
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleTaskUpdate = async () => {
    // Reload the task data after update
    await loadTaskData();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'in-progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'todo': return Circle;
      case 'in-progress': return PlayCircle;
      case 'completed': return CheckCircle;
      default: return Circle;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return null;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" disabled>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tasks
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="animate-pulse">
              <CardHeader>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="flex space-x-2">
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Task Not Found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The task you're looking for doesn't exist or has been deleted.</p>
        <Button onClick={() => navigate('/product-manager/tasks')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tasks
        </Button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(task.status);
  const isOverdue = task.dueDate && task.status !== 'completed' &&
    (task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate as any)) < new Date();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/product-manager/tasks')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tasks
        </Button>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEditDialog(true)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Task
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Details */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStatusChange(
                      task.status === 'completed' ? 'todo' : 
                      task.status === 'todo' ? 'in-progress' : 'completed'
                    )}
                    disabled={updating}
                    className="p-1 mt-1"
                  >
                    <StatusIcon className={`h-6 w-6 ${
                      task.status === 'completed' ? 'text-green-600' :
                      task.status === 'in-progress' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                  </Button>
                  <div className="flex-1">
                    <CardTitle className={`text-2xl ${
                      task.status === 'completed' ? 'line-through text-gray-500' : ''
                    }`}>
                      {task.title}
                    </CardTitle>
                    {project && (
                      <div className="flex items-center mt-2">
                        <FolderOpen className="h-4 w-4 mr-1 text-gray-500" />
                        <Badge variant="outline">
                          {project.name}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Status and Priority */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                  <Badge className={getStatusColor(task.status)}>
                    {task.status.replace('-', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority:</span>
                  <Badge className={getPriorityColor(task.priority)}>
                    {task.priority}
                  </Badge>
                </div>
              </div>

              <Separator />

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Description</h3>
                <CardDescription className="text-base whitespace-pre-wrap">
                  {task.description ? (
                    <LinkifiedText text={task.description} />
                  ) : (
                    'No description provided.'
                  )}
                </CardDescription>
              </div>

              {/* Images */}
              {task.images && task.images.length > 0 && (
                <>
                  <Separator />
                  <ImageGallery
                    images={task.images}
                    canDelete={false}
                    showDownload={true}
                  />
                </>
              )}

              {/* Tags */}
              {task.tags && task.tags.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Tag className="h-4 w-4 mr-2" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {task.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <CommentThread taskId={task.id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Task Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Task Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Assignee */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Assignee</span>
                <div className="flex items-center text-sm">
                  {task.assignedTo ? (
                    <>
                      <User className="h-4 w-4 mr-1" />
                      {assigneeDisplayName}
                    </>
                  ) : (
                    <span className="text-gray-500">Unassigned</span>
                  )}
                </div>
              </div>

              {/* Due Date */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Due Date</span>
                <div className={`flex items-center text-sm ${
                  isOverdue ? 'text-red-500' : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {task.dueDate ? (
                    <>
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(task.dueDate)}
                      {isOverdue && <span className="ml-1">(Overdue)</span>}
                    </>
                  ) : (
                    <span className="text-gray-500">No due date</span>
                  )}
                </div>
              </div>

              {/* Created */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Created</span>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Clock className="h-4 w-4 mr-1" />
                  {formatTimestamp(task.createdAt)}
                </div>
              </div>

              {/* Created By */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Created By</span>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <User className="h-4 w-4 mr-1" />
                  {creatorDisplayName}
                </div>
              </div>

              {/* Completed */}
              {task.completedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Completed</span>
                  <div className="flex items-center text-sm text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {formatTimestamp(task.completedAt)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handleStatusChange('todo')}
                disabled={updating || task.status === 'todo'}
              >
                <Circle className="h-4 w-4 mr-2" />
                Mark as To Do
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handleStatusChange('in-progress')}
                disabled={updating || task.status === 'in-progress'}
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Mark as In Progress
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handleStatusChange('completed')}
                disabled={updating || task.status === 'completed'}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Completed
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Task"
        description={`Are you sure you want to delete "${task.title}"? This action cannot be undone and will also delete all comments.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {/* Edit Task Dialog */}
      <EditTaskDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        task={task}
        projects={projects}
        onTaskUpdated={handleTaskUpdate}
      />
    </div>
  );
};

export default TaskDetail;